import { useEffect, useMemo } from 'react';

import observer from 'saddlebag-observer';

import { TEXT_STYLES } from '@skyscanner/backpack-web/bpk-component-text';

import ReviewProviders from './ReviewProviders';
import ReviewsCustomizeSection from './ReviewsCustomizeSection';
import ReviewsRatingSection from './ReviewsRatingSection';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';

import { useHotelBasicInfo } from '@hotels-hotel-detail/data-access/hooks/useHotelContent';
import { useReviews } from '@hotels-hotel-detail/data-access/hooks/useHotelReviewsContext';
import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';

type Props = {
  miniEventTracker: MiniEventTracker;
};

const HotelReviews = ({ miniEventTracker }: Props) => {
  const reviews = useReviews();
  const { reviewSummary } = useHotelBasicInfo();

  useEffect(() => {
    miniEventTracker.trackExperimentChokepoint(
      'hotels_details_review_optimization_chokepoint',
    );
  }, [miniEventTracker]);

  const hasReviewData = useMemo(() => {
    const hasReviews = reviews && reviews.length > 0;
    return hasReviews;
  }, [reviews]);

  useEffect(() => {
    observer.publish('hotel-reviews-result-is-empty', {
      isReviewsEmpty: !hasReviewData,
    });
  }, [hasReviewData]);

  if (!hasReviewData) {
    return null;
  }

  return (
    <>
      <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
        {(isMobile: boolean) =>
          isMobile ? (
            <>
              <ReviewsRatingSection reviewSummary={reviewSummary} />
              <ReviewProviders height={24} textStyle={TEXT_STYLES.heading4} />
            </>
          ) : null
        }
      </BpkBreakpoint>
      <ReviewsCustomizeSection reviewSummary={reviewSummary} />
    </>
  );
};

export default withMiniEventTracker(HotelReviews);
